import { useState, type FC } from 'react';
import { Modal, Input, Select, DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';

const CATEGORIES = [
  { value: 'content', label: 'Content' },
  { value: 'seo', label: 'SEO' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'sales', label: 'Sales' },
  { value: 'referral', label: 'Referral' },
  { value: 'ops', label: 'Ops' },
  { value: 'launch', label: 'Launch' },
];

const MODULES = [
  { value: '', label: '-- Aucun --' },
  { value: 'blog-manager', label: 'Blog Manager' },
  { value: 'content-calendar', label: 'Content Calendar' },
  { value: 'campaigns', label: 'Campaigns' },
  { value: 'content-factory', label: 'Content Factory' },
  { value: 'sales-intelligence', label: 'Sales Intelligence' },
  { value: 'referral-dashboard', label: 'Referral Network' },
  { value: 'marketing-dashboard', label: 'Marketing Dashboard' },
];

export interface MilestoneFormData {
  title: string;
  description: string;
  category: string;
  dueDate: string | null;
  linkedModule: string | null;
  linkedEntityId: string | null;
  assignee: string | null;
}

interface CreateMilestoneModalProps {
  open: boolean;
  loading?: boolean;
  onOk: (data: MilestoneFormData) => void;
  onCancel: () => void;
  initialData?: Partial<MilestoneFormData>;
  title?: string;
}

const CreateMilestoneModal: FC<CreateMilestoneModalProps> = ({
  open, loading, onOk, onCancel, initialData, title = 'Nouveau jalon',
}) => {
  const [form, setForm] = useState<MilestoneFormData>({
    title: initialData?.title ?? '',
    description: initialData?.description ?? '',
    category: initialData?.category ?? 'content',
    dueDate: initialData?.dueDate ?? null,
    linkedModule: initialData?.linkedModule ?? null,
    linkedEntityId: initialData?.linkedEntityId ?? null,
    assignee: initialData?.assignee ?? null,
  });

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    onOk(form);
  };

  return (
    <Modal
      title={title}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      okText="Enregistrer"
      cancelText="Annuler"
      destroyOnClose
    >
      <div className="space-y-4 pt-2">
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Titre *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="Ex: Rediger 10 articles blog"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Description</label>
          <Input.TextArea
            rows={2}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Categorie *</label>
            <Select
              className="w-full"
              options={CATEGORIES}
              value={form.category}
              onChange={(v) => setForm((p) => ({ ...p, category: v }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Echeance</label>
            <DatePicker
              className="w-full"
              onChange={(_: Dayjs | null, dateStr: string | string[]) => {
                const value = Array.isArray(dateStr) ? dateStr[0] : dateStr;
                setForm((p) => ({ ...p, dueDate: value || null }));
              }}
              format="YYYY-MM-DD"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Module lie</label>
            <Select
              className="w-full"
              options={MODULES}
              value={form.linkedModule ?? ''}
              onChange={(v) => setForm((p) => ({ ...p, linkedModule: v || null }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Responsable</label>
            <Input
              value={form.assignee ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, assignee: e.target.value || null }))}
              placeholder="Nom ou email"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateMilestoneModal;
